package fr.manyverse.app.presentation.viewmodel.gamemaster

import fr.manyverse.app.domain.model.EventType
import fr.manyverse.app.domain.model.GameEvent
import fr.manyverse.app.domain.usecase.GetTableEventsUseCase
import fr.manyverse.app.presentation.state.AsyncUiState
import fr.manyverse.app.presentation.viewmodel.launchAsyncMapped
import kotlin.time.Clock
import kotlin.time.Instant
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch

private const val MESSAGES_FETCH_LIMIT = 100

/**
 * Owns the GameMaster private-message list, its pagination and unread counter.
 * Extracted from GameMasterViewModel; behavior is intentionally identical.
 */
class GameMasterMessagesDelegate(
    private val scope: CoroutineScope,
    private val tableId: String,
    private val currentUserId: String,
    private val getTableEventsUseCase: GetTableEventsUseCase
) {
    private val _messages = MutableStateFlow<List<GameEvent>>(emptyList())
    val messages: StateFlow<List<GameEvent>> = _messages.asStateFlow()

    private val _hasMoreOlderMessages = MutableStateFlow(false)
    val hasMoreOlderMessages: StateFlow<Boolean> = _hasMoreOlderMessages.asStateFlow()

    private val _loadMoreMessagesState = MutableStateFlow<AsyncUiState<Unit>>(AsyncUiState.Idle)
    val loadMoreMessagesState: StateFlow<AsyncUiState<Unit>> = _loadMoreMessagesState.asStateFlow()

    // Compteur incrémental : +1 sur chaque MESSAGE_SENT Realtime venant d'un autre user,
    // reset à clearUnreadMessages. Évite l'ancien combine + count O(n) sur la liste
    // complète (jusqu'à plusieurs centaines de messages × N inserts).
    private val _unreadMessageCount = MutableStateFlow(0)
    val unreadMessageCount: StateFlow<Int> = _unreadMessageCount.asStateFlow()

    // Initialisé à la création : l'historique chargé est considéré comme déjà lu, seuls
    // les messages reçus via Realtime après ce point comptent comme non lus.
    private val _lastReadMessageAt = MutableStateFlow<Instant>(Clock.System.now())

    private var latestMessageAt: Instant? = null

    fun loadMessages() {
        scope.launch {
            val since = latestMessageAt
            getTableEventsUseCase(
                tableId = tableId,
                limit = MESSAGES_FETCH_LIMIT,
                offset = 0,
                eventTypes = listOf(EventType.MESSAGE_SENT),
                since = since
            ).onSuccess { events ->
                if (events.isEmpty()) return@onSuccess
                latestMessageAt = events.maxOf { it.createdAt }
                // Au tout premier chargement (since == null), si on a atteint la limite,
                // c'est qu'il existe potentiellement des messages plus anciens à paginer.
                if (since == null) {
                    _hasMoreOlderMessages.value = events.size >= MESSAGES_FETCH_LIMIT
                }
                _messages.update { current ->
                    if (since == null) {
                        events.sortedByDescending { it.createdAt }
                    } else {
                        val existingIds = current.mapTo(HashSet()) { it.id }
                        val incoming = events.filter { it.id !in existingIds }
                        (incoming + current).sortedByDescending { it.createdAt }
                    }
                }
            }
        }
    }

    /**
     * Paginates older messages on demand (UI scroll-to-top).
     * Idempotent : skip if already loading or no more older messages available.
     */
    fun loadOlderMessages() {
        if (_loadMoreMessagesState.value is AsyncUiState.Loading) return
        if (!_hasMoreOlderMessages.value) return
        scope.launchAsyncMapped(
            _loadMoreMessagesState,
            {
                getTableEventsUseCase(
                    tableId = tableId,
                    limit = MESSAGES_FETCH_LIMIT,
                    offset = _messages.value.size,
                    eventTypes = listOf(EventType.MESSAGE_SENT)
                )
            }
        ) { older ->
            _hasMoreOlderMessages.value = older.size >= MESSAGES_FETCH_LIMIT
            if (older.isNotEmpty()) {
                _messages.update { current ->
                    val existingIds = current.mapTo(HashSet()) { it.id }
                    val incoming = older.filter { it.id !in existingIds }
                    (current + incoming).sortedByDescending { it.createdAt }
                }
            }
        }
    }

    /**
     * Handles a Realtime MESSAGE_SENT event: always prepends it to the list and bumps the
     * unread counter when it comes from another user.
     *
     * NB : on ne met PAS à jour latestMessageAt depuis Realtime. Sinon, un message Realtime
     * arrivant entre l'envoi et la résolution du prochain loadMessages() avancerait le
     * `since` et masquerait des messages que le fetch n'aurait pas encore vus. Le re-fetch
     * dédupliquera par id (cf. loadMessages).
     */
    fun onRealtimeMessage(event: GameEvent) {
        _messages.update { listOf(event) + it }
        if (event.triggeredBy != currentUserId) {
            _unreadMessageCount.update { it + 1 }
        }
    }

    fun clearUnreadMessages() {
        _unreadMessageCount.value = 0
        val latest = _messages.value.maxOfOrNull { it.createdAt } ?: return
        if (latest > _lastReadMessageAt.value) {
            _lastReadMessageAt.value = latest
        }
    }
}
